<script setup lang="ts">
import { SerializedChartType } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import { computed, Ref, ref } from 'vue'
import SerializedChart from '@/composables/charts/influx-charts/SerializedChart.vue'
import { ChartSuggestion, useChartSuggestions } from '@/components/charts/chart-creator/ChartSuggestions'
import { useI18n } from 'vue-i18n'

const emit = defineEmits<{ selected: [chart: SerializedChartType | null] }>()

const { t } = useI18n()
const chartSuggestions = useChartSuggestions()

const searchQuery: Ref<string> = ref('')
const selectedChart: Ref<ChartSuggestion | null> = ref(null)
const chartPreview = ref<HTMLElement | null>(null)

const filteredChartSuggestions = computed(() => {
  if (!searchQuery.value) {
    return chartSuggestions
  }

  const query = searchQuery.value.toLowerCase()
  return chartSuggestions.filter(suggestion => suggestion.title.toLowerCase().includes(query) || suggestion.description.toLowerCase().includes(query))
})

const isSelected = (suggestion: ChartSuggestion) => selectedChart.value?.id === suggestion.id

const pickChart = (id: string | null) => {
  if (id === null) {
    selectedChart.value = null
    emit('selected', null)
    return
  }

  selectedChart.value = chartSuggestions.find(suggestion => suggestion.id === id) || null
  emit('selected', selectedChart.value ? selectedChart.value.serializedChart : null)

  requestAnimationFrame(() => {
    chartPreview.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    })
  })
}
</script>

<template>
  <v-text-field
    v-model="searchQuery"
    prepend-icon="mdi-magnify"
    :placeholder="t('chart_sharing.creator.suggestion.search_suggestions')"
    variant="outlined"
    clearable
    class="mx-4 my-2"
  />

  <v-row
    class="mb-2"
    justify="space-around"
  >
    <v-col
      v-for="(suggestion, index) in filteredChartSuggestions"
      :key="index"
      class="flex-grow-1 flex-shrink-0"
      style="min-width: 300px; max-width: 100%; flex-basis: calc(33.33% - 24px)"
    >
      <v-layout-card
        class="py-2 h-100 border"
        :class="{ 'border-md border-primary border-opacity-75': isSelected(suggestion) }"
        :variant="isSelected(suggestion) ? 'outlined' : undefined"
        :elevation="isSelected(suggestion) ? 4 : 2"
        :color="isSelected(suggestion) ? 'primary' : undefined"
        :ripple="false"
        @click="pickChart(isSelected(suggestion) ? null : suggestion.id)"
      >
        <v-card-title>
          <v-avatar
            :color="isSelected(suggestion) ? 'primary' : 'surface'"
            size="small"
            start
          >
            <v-icon
              :class="{ 'opacity-70': !isSelected(suggestion) }"
              size="x-small"
              icon="mdi-chart-line"
            />
          </v-avatar>
          <span class="text-subtitle-1 font-weight-medium">
            {{ suggestion.title }}
          </span>
        </v-card-title>
        <v-card-text :class="{ 'opacity-70': !isSelected(suggestion) }">{{ suggestion.description }}</v-card-text>
      </v-layout-card>
    </v-col>
  </v-row>

  <v-divider
    v-if="selectedChart"
    class="my-6"
    thickness="2"
  />

  <div
    v-if="selectedChart"
    ref="chartPreview"
  >
    <SerializedChart :serialized="Object.assign({}, selectedChart.serializedChart, { defaultCollapsed: false })" />
  </div>
</template>

<style scoped></style>
